import { Button } from '@/components/ui/button';
import { ChevronLeft, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

interface GuidedStudyNavigationProps {
    isDarkMode: boolean;
    isFirstStep: boolean;
    isLastStep: boolean;
    canProceed: boolean;
    disabledReason: string | null;
    currentStepOptional?: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onComplete: () => void;
}

export function GuidedStudyNavigation({
    isDarkMode,
    isFirstStep,
    isLastStep,
    canProceed,
    disabledReason,
    currentStepOptional,
    onPrevious,
    onNext,
    onComplete,
}: GuidedStudyNavigationProps) {
    return (
        <div className={`relative z-20 border-t ${isDarkMode ? 'border-amber-900/20 bg-[#0a0a0b]/40 backdrop-blur-sm' : 'border-amber-100 bg-[#fdfbf7]/40 backdrop-blur-sm'}`}>
            <div className="container mx-auto px-4 py-1.5 max-w-4xl">
                <div className="flex items-center justify-between">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onPrevious}
                        disabled={isFirstStep}
                        className={`text-xs font-bold uppercase tracking-widest gap-2 h-9 ${isDarkMode ? 'text-slate-400 hover:text-amber-500' : 'text-slate-600 hover:text-amber-600'}`}
                    >
                        <ChevronLeft className="h-4 w-4" />
                        Backtrack
                    </Button>

                    <div className="flex items-center gap-3">
                        {currentStepOptional && !isLastStep && (
                            <Button variant="ghost" size="sm" onClick={onNext} className="text-xs text-muted-foreground hover:text-foreground h-9">
                                Skip Point
                            </Button>
                        )}
                        <div className="flex flex-col items-end gap-1">
                            {!canProceed && disabledReason && (
                                <motion.div
                                    initial={{ opacity: 0, x: 10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className={`border px-3 py-1.5 rounded-full flex items-center gap-2 shadow-sm ${isDarkMode ? 'bg-amber-950/40 border-amber-800/30' : 'bg-amber-100 border-amber-200'}`}
                                >
                                    <Sparkles className="h-3 w-3 text-amber-600 animate-pulse" />
                                    <span className={`text-[10px] font-bold uppercase tracking-tight ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                                        {disabledReason}
                                    </span>
                                </motion.div>
                            )}
                            <Button
                                variant="default"
                                size="sm"
                                onClick={isLastStep ? onComplete : onNext}
                                disabled={!canProceed}
                                className={`text-xs font-bold uppercase tracking-widest px-6 shadow-lg h-9 transition-all ${isDarkMode ? 'bg-amber-600 hover:bg-amber-700 text-amber-50 border-0' : 'bg-primary hover:bg-primary/90'} ${!canProceed ? 'opacity-40 grayscale-[0.5]' : ''}`}
                            >
                                {isLastStep ? 'Complete Expedition' : 'Continue Discovery'}
                                {!isLastStep && <ArrowRight className="h-4 w-4 ml-2" />}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
